import { useEffect, useState } from 'react';
import { Button, Card, Empty, Flex, Image, Tag, Typography } from 'antd';
import { PhysicalImage } from '../../components/PhysicalSize';
import { STAGE_LABEL, won } from '../../lib/generation-types';
import { SHORTCUT_HELP, VERDICT_LABEL, shortcutFor, type ReviewAsset, type Verdict } from '../../lib/review-types';
import { VocabHint, type VocabHintData } from '../../components/VocabHint';

const CHECKER = 'repeating-conic-gradient(#f3f3f3 0% 25%, #fff 0% 50%) 50% / 16px 16px';
const BUTTONS: { v: Verdict; key: string }[] = [
  { v: 'default', key: '1' },
  { v: 'search', key: '2' },
  { v: 'both', key: '3' },
  { v: 'rework', key: 'R' },
  { v: 'discard', key: 'D' },
];

/**
 * 연속 검수: 한 장씩 크게(최종·3cm·5cm) 보며 키보드로 판정하고 다음 장으로 넘어간다.
 * 판정한 이미지는 목록에서 빠지므로 같은 위치에 다음 이미지가 온다.
 */
export function ContinuousReview({ assets, names, hints, onVerdict, busy, paused }: {
  assets: ReviewAsset[];
  names: Map<string, string>;
  /** 뜻 확인 힌트(슬러그·키워드) */
  hints?: Map<string, VocabHintData>;
  onVerdict: (ids: string[], v: Verdict) => void;
  busy: boolean;
  /** 사유 입력 창 등이 열려 있으면 단축키를 멈춘다 */
  paused: boolean;
}) {
  const [index, setIndex] = useState(0);
  const [source, setSource] = useState<'final' | 'original'>('final');
  const i = Math.min(index, Math.max(0, assets.length - 1));
  const a = assets[i];

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (paused || busy || !a) return;
      const act = shortcutFor(e);
      if (!act) return;
      e.preventDefault();
      if (act === 'next') setIndex(Math.min(i + 1, assets.length - 1));
      else if (act === 'prev') setIndex(Math.max(i - 1, 0));
      else if (act === 'toggleSource') setSource((s) => (s === 'final' ? 'original' : 'final'));
      else onVerdict([a.assetId], act);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [a, i, assets.length, paused, busy, onVerdict]);

  if (!a) return <Empty description="검수할 이미지를 모두 판정했습니다" />;
  const name = names.get(a.vocabIds[0]) ?? a.vocabIds[0];
  const src = (source === 'final' ? a.finalUrl : a.originalUrl) ?? a.originalUrl ?? a.finalUrl;
  const stage = STAGE_LABEL[a.stage] ?? { label: a.stage, color: 'default' };
  return (
    <Card size="small" data-testid="continuous">
      <Flex justify="space-between" align="center" wrap gap={8} style={{ marginBottom: 12 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          {name} <VocabHint hint={hints?.get(a.vocabIds[0])} /> <Typography.Text type="secondary" style={{ fontSize: 13, fontWeight: 'normal' }}>{i + 1} / {assets.length} · {source === 'final' ? '최종 512' : '원본'}</Typography.Text>
        </Typography.Title>
        <Tag color={stage.color}>{stage.label}</Tag>
      </Flex>
      {src ? (
        <Flex wrap gap={24} align="flex-end">
          <Image src={src} width={320} height={320} style={{ objectFit: 'contain', background: CHECKER }} alt={name} />
          <PhysicalImage src={src} alt={`${name} 3cm`} size={3} caption="3cm" />
          <PhysicalImage src={src} alt={`${name} 5cm`} size={5} caption="5cm" />
        </Flex>
      ) : <Empty description="이미지 없음" />}
      <Typography.Text type="secondary" style={{ display: 'block', margin: '8px 0', fontSize: 12 }}>
        {a.origin === 'manual' ? '수동 제작 · ' : ''}{a.generation?.modelId.replace('openai/', '') ?? '-'}{a.generation?.costKrw != null ? ` · ${won(a.generation.costKrw)}` : ''}
        {a.review.verdict ? ` · 현재 판정 ${VERDICT_LABEL[a.review.verdict]}${a.review.note ? `(${a.review.note})` : ''}` : ''}
      </Typography.Text>
      <Flex wrap gap={8}>
        {BUTTONS.map((b) => (
          <Button key={b.v} danger={b.v === 'discard'} type={b.v === 'search' ? 'primary' : 'default'} disabled={busy} onClick={() => onVerdict([a.assetId], b.v)}>
            {VERDICT_LABEL[b.v]} ({b.key})
          </Button>
        ))}
        <Button disabled={i === 0} onClick={() => setIndex(i - 1)}>← 이전</Button>
        <Button disabled={i >= assets.length - 1} onClick={() => setIndex(i + 1)}>다음 →</Button>
      </Flex>
      <Typography.Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>단축키: {SHORTCUT_HELP}. 연속 검수에서 판정하면 3cm 인지 여부를 함께 기록합니다(승인 = 인지 가능).</Typography.Text>
    </Card>
  );
}
